import { useMemo } from "react";

import { filterTruthy, ALL_VALUE, NullableRecord, filterNullableRecord } from "@bridgesplit/utils";

import { useFarmingInfosQuery } from "../reducers";
import {
    ActionType,
    FarmingInfoExpanded,
    PartnerRewardExpanded,
    PartnerInfoExpanded,
    PartnerPointExpanded,
    Partner,
    FarmingTypeKind,
    LendFarmingInfo,
    LoopFarmingInfo,
    BorrowFarmingInfo
} from "../types";
import { BsMetaUtil } from "../utils";
import { useLendVaults } from "./vaults";
import { useLoopsExpanded } from "./loop";
import { LoopscalePointsAction } from "../types/points";
import { useFarmingInfosContext } from "./farming";
import { MAXIMUM_PAGE_SIZE } from "../constants";
import { usePartnersInfo } from "./partners";
import { useTokenListContext } from "./token-list";
import { useAbfTypesToUiConverter } from "../utils/ui";

const ACTION_TYPE_TO_LOOPSCALE_POINTS: Record<ActionType, LoopscalePointsAction> = {
    [ActionType.Lend]: LoopscalePointsAction.LiquidStake,
    [ActionType.Borrow]: LoopscalePointsAction.Borrow,
    [ActionType.Loop]: LoopscalePointsAction.Borrow
};

// Type guards for FarmingInfoExpanded discriminated union
export function isLoopFarmingInfo(farmingInfo: FarmingInfoExpanded | undefined): farmingInfo is LoopFarmingInfo {
    return farmingInfo?.kind === FarmingTypeKind.Loop;
}

export function isLendFarmingInfo(farmingInfo: FarmingInfoExpanded | undefined): farmingInfo is LendFarmingInfo {
    return farmingInfo?.kind === FarmingTypeKind.Lend;
}

export function isBorrowFarmingInfo(farmingInfo: FarmingInfoExpanded | undefined): farmingInfo is BorrowFarmingInfo {
    return farmingInfo?.kind === FarmingTypeKind.Borrow;
}

export function useFarmingInfos(): { ecosystems: FarmingInfoExpanded[] | undefined; isLoading: boolean } {
    const { data: farmingInfosResponse, isLoading: farmingInfosLoading } = useFarmingInfosQuery();
    const { partners, isLoading: partnersLoading } = usePartnersInfo();
    const { convertFarmingInfo, convertPartnerPoint, convertPartnerReward } = useAbfTypesToUiConverter();

    // The ecosystems map only ever joins vaults/loops whose address equals a farming integrationId
    // (see lendVaultToAddress.get(integrationId) below). Fetch only those — not the full vault/loop
    // universe — since this provider is global and mounts on every route. Skip until the integration
    // list resolves so we never fall back to "no filter" (empty array = full list).
    const integrationIds = useMemo(
        () => farmingInfosResponse?.data?.map((i) => i.integrationId) ?? [],
        [farmingInfosResponse]
    );
    const skipVaultFetch = integrationIds.length === 0;

    const { data: lendVaults, isLoading: lendVaultsLoading } = useLendVaults({
        filter: {
            vaultAddresses: integrationIds,
            pageSize: MAXIMUM_PAGE_SIZE
        },
        skip: skipVaultFetch,
        skipTimelocks: true
    });
    const { data: loopVaults, isLoading: loopVaultsLoading } = useLoopsExpanded(
        { loopVaults: integrationIds, showHidden: false },
        { skip: skipVaultFetch }
    );

    // Create lookup maps
    const partnerIdToPartner = useMemo(() => {
        if (!partners) return undefined;
        return new Map(partners.map((p) => [p.partner.id, p]));
    }, [partners]);

    // the return from lendvaults is unstable, returning a new reference every time
    // once we have the addresses just look to see if those change. if they do update the map
    // TODO: we should fix lendVaultsMap

    const { getMetadata } = useTokenListContext();
    const stableLoopVaultIdentifiers = loopVaults
        ?.map((loop) => loop.vaultIdentifier)
        .sort()
        .join(",");

    const lendVaultToAddress = useMemo(() => {
        if (!lendVaults) return undefined;
        return new Map(lendVaults.map((vault) => [vault.vault.address, vault]));
    }, [lendVaults]);

    const loopVaultToAddress = useMemo(() => {
        if (!stableLoopVaultIdentifiers || !loopVaults) return undefined;
        return new Map(loopVaults.map((loop) => [loop.vaultIdentifier, loop]));
    }, [stableLoopVaultIdentifiers, loopVaults]);

    const ecosystems = useMemo(() => {
        // No integrations → no vault/loop fetch (skipped), so the *toAddress maps stay undefined. Resolve
        // to [] here rather than falling through to the undefined guard, which would hang consumers
        // (ecosystems undefined while isLoading is already false).
        if (farmingInfosResponse?.data?.length === 0) return [];
        if (!farmingInfosResponse?.data || !partnerIdToPartner || !lendVaultToAddress || !loopVaultToAddress) {
            return undefined;
        }

        return farmingInfosResponse.data
            .map((rawIntegration): NullableRecord<FarmingInfoExpanded> => {
                // Step 1: Convert/normalize backend data
                const farmingInfo = convertFarmingInfo(rawIntegration);

                // Step 2: Build expanded type with frontend enrichment
                const partners: PartnerInfoExpanded[] = farmingInfo.partners
                    .map((partnerId) => partnerIdToPartner.get(partnerId))
                    .filter(filterTruthy);

                // Convert partner points and enrich with partner data
                const partnerPoints: PartnerPointExpanded[] = farmingInfo.partnerPoints.map((rawPoint) => {
                    const point = convertPartnerPoint(rawPoint);
                    return {
                        ...point,
                        partner: partnerIdToPartner.get(point.partnerId)
                    };
                });

                // Convert partner rewards and enrich with partner data
                const partnerRewards: PartnerRewardExpanded[] = farmingInfo.partnerRewards.map((rawReward) => {
                    const reward = convertPartnerReward(rawReward);
                    return {
                        ...reward,
                        partner: partnerIdToPartner.get(reward.partnerId)
                    };
                });

                // Determine integration type and get vaults
                const lendVault = lendVaultToAddress.get(farmingInfo.integrationId);
                const loopVault = loopVaultToAddress.get(farmingInfo.integrationId);

                // Build discriminated union based on vault type
                if (loopVault) {
                    return {
                        ...farmingInfo,
                        kind: FarmingTypeKind.Loop,
                        integrationType: ActionType.Loop,
                        partners,
                        partnerPoints,
                        partnerRewards,
                        loopscaleReward: ACTION_TYPE_TO_LOOPSCALE_POINTS[ActionType.Loop],
                        loopVault
                    };
                }

                if (lendVault) {
                    return {
                        ...farmingInfo,
                        kind: FarmingTypeKind.Lend,
                        integrationType: ActionType.Lend,
                        partners,
                        partnerPoints,
                        partnerRewards,
                        loopscaleReward: ACTION_TYPE_TO_LOOPSCALE_POINTS[ActionType.Lend],
                        lendVault
                    };
                }

                //We fallback to borrow if we don't have a lend or loop vault
                return {
                    ...farmingInfo,
                    kind: FarmingTypeKind.Borrow,
                    integrationType: ActionType.Borrow,
                    partners,
                    partnerPoints,
                    partnerRewards,
                    loopscaleReward: ACTION_TYPE_TO_LOOPSCALE_POINTS[ActionType.Borrow],
                    metadata: getMetadata(partnerPoints[0]?.integrationTargetMint)
                };
            })
            .filter(filterNullableRecord);
    }, [
        farmingInfosResponse,
        partnerIdToPartner,
        lendVaultToAddress,
        loopVaultToAddress,
        convertFarmingInfo,
        convertPartnerPoint,
        convertPartnerReward,
        getMetadata
    ]);

    const isLoading = lendVaultsLoading || loopVaultsLoading || farmingInfosLoading || partnersLoading;

    return { ecosystems, isLoading };
}

export function useEcosystemsRowsPerPartnerActionType(selectedPartnerId?: string): {
    rowsByActionType: { actionType: ActionType; rows: FarmingInfoExpanded[] | undefined }[];
    isLoading: boolean;
} {
    const { ecosystems, isLoading } = useFarmingInfosContext();
    const { getMetadata } = useTokenListContext();

    const rowsByActionType = useMemo(() => {
        if (!ecosystems) {
            return [
                { actionType: ActionType.Lend, rows: undefined },
                { actionType: ActionType.Loop, rows: undefined }
            ];
        }

        // Build separate arrays for each action type
        const lendRows: FarmingInfoExpanded[] = [];
        const loopRows: FarmingInfoExpanded[] = [];
        const borrowRows: FarmingInfoExpanded[] = [];

        for (const integration of ecosystems) {
            // For each partner, create a separate row
            for (const partner of integration.partners) {
                // Filter by selected partner if specified
                if (selectedPartnerId && selectedPartnerId !== ALL_VALUE && partner.partner.id !== selectedPartnerId) {
                    continue;
                }

                // Filter points for this partner
                const partnerPoints = integration.partnerPoints.filter(
                    (point) => point.partner?.partner.id === partner.partner.id
                );

                // Filter rewards for this partner
                const partnerRewards = integration.partnerRewards.filter(
                    (reward) => reward.partner?.partner.id === partner.partner.id
                );

                // Skip if no data for this partner
                if (partnerPoints.length === 0 && partnerRewards.length === 0) {
                    continue;
                }

                // Create a row with filtered partner data - spread preserves the discriminated union type
                const row: FarmingInfoExpanded = {
                    ...integration,
                    partners: [partner],
                    partnerPoints,
                    partnerRewards
                };

                if (row.integrationType === ActionType.Lend) {
                    lendRows.push(row);
                } else if (row.integrationType === ActionType.Loop) {
                    loopRows.push(row);
                } else if (row.integrationType === ActionType.Borrow) {
                    // For borrow, we use the integration target mint symbol as the integration name
                    const targetMint = partnerPoints[0]?.integrationTargetMint;
                    const metadata = targetMint ? getMetadata(targetMint) : undefined;
                    const symbol = metadata ? BsMetaUtil.getSymbol(metadata) : undefined;

                    borrowRows.push({
                        ...row,
                        integrationName: symbol || row.integrationName
                    });
                }
            }
        }

        // Only return action types that have data
        const result: { actionType: ActionType; rows: FarmingInfoExpanded[] }[] = [];

        if (lendRows.length > 0) {
            result.push({ actionType: ActionType.Lend, rows: lendRows });
        }
        if (loopRows.length > 0) {
            result.push({ actionType: ActionType.Loop, rows: loopRows });
        }
        if (borrowRows.length > 0) {
            result.push({ actionType: ActionType.Borrow, rows: borrowRows });
        }

        return result;
    }, [ecosystems, getMetadata, selectedPartnerId]);

    return { rowsByActionType, isLoading };
}

export function getFirstPointsIntegration(
    farmingInfo: FarmingInfoExpanded | undefined
): PartnerPointExpanded | undefined {
    if (!farmingInfo) return undefined;
    return farmingInfo.partnerPoints[0];
}

export function getFirstPartnerReward(farmingInfo: FarmingInfoExpanded | undefined) {
    if (!farmingInfo) return undefined;
    return farmingInfo.partnerRewards?.[0];
}

export function getFirstPartnerName(farmingInfo: FarmingInfoExpanded): string {
    const firstPartner = getFirstPartner(farmingInfo);
    return firstPartner?.rewardName ?? firstPartner?.id ?? "";
}

export function getFirstPartner(farmingInfo: FarmingInfoExpanded): Partner | undefined {
    return farmingInfo.partners[0]?.partner ?? undefined;
}
